// /opt/isp_erp/frontend/src/components/tickets/ClienteBitacora.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Comment as CommentIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Engineering as EngineeringIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ticketsAPI } from '../../api/tickets.api';
import api from '../../api/axios';

export default function ClienteBitacora({ open, onClose, customerId, customerName }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);

  useEffect(() => {
    if (open && customerId) {
      loadClienteBitacora();
    }
  }, [open, customerId]);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchTerm, tabValue]);

  const loadClienteBitacora = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/tickets/cliente/${customerId}`);
      setTickets(response.data || []);
      setFilteredTickets(response.data || []);
    } catch (err) {
      console.error('Error loading cliente bitacora:', err);
      setError(err.response?.data?.detail || 'Error al cargar la bitácora del cliente');
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = [...tickets];

    // Filtrar por estado
    if (tabValue === 1) {
      filtered = filtered.filter(t => 
        t.status === 'abierto' || t.status === 'en_diagnostico' || t.status === 'asignado_campo'
      );
    } else if (tabValue === 2) {
      filtered = filtered.filter(t => t.status === 'resuelto');
    } else if (tabValue === 3) {
      filtered = filtered.filter(t => t.status === 'cerrado');
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.ticket_number?.toLowerCase().includes(term) ||
        t.title?.toLowerCase().includes(term) ||
        t.assigned_technician_name?.toLowerCase().includes(term) ||
        t.status?.toLowerCase().includes(term) ||
        t.ticket_type?.toLowerCase().includes(term)
      );
    }

    setFilteredTickets(filtered);
  };

  const getStatusColor = (status) => {
    const colors = {
      abierto: 'default',
      en_diagnostico: 'primary',
      asignado_campo: 'warning',
      resuelto: 'success',
      cerrado: 'secondary',
      cancelado: 'error'
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      abierto: 'Abierto',
      en_diagnostico: 'En Diagnóstico',
      asignado_campo: 'Asignado a Campo',
      resuelto: 'Resuelto',
      cerrado: 'Cerrado',
      cancelado: 'Cancelado'
    };
    return labels[status?.toLowerCase()] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      baja: 'success',
      media: 'info',
      alta: 'warning',
      critica: 'error'
    };
    return colors[priority?.toLowerCase()] || 'default';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: es });
    } catch {
      return dateStr;
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
  };

  const getTicketTypeIcon = (type) => {
    const types = {
      instalacion: '📦',
      retiro: '📤',
      cambio_equipo: '🔄',
      incidente: '⚠️'
    };
    return types[type] || '📋';
  };

  const getTicketTypeLabel = (type) => {
    const labels = {
      instalacion: 'Instalación',
      retiro: 'Retiro',
      cambio_equipo: 'Cambio Equipo',
      incidente: 'Incidente'
    };
    return labels[type] || type;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Bitácora del Cliente</Typography>
            {customerName && (
              <Chip 
                label={customerName} 
                size="small" 
                color="primary" 
                variant="outlined" 
                icon={<PersonIcon />}
              />
            )}
            <Chip 
              label={`${tickets.length} tickets`} 
              size="small" 
              color="info" 
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadClienteBitacora}
              sx={{ ml: 'auto' }}
            >
              Actualizar
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              {/* Buscador */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      placeholder="Buscar por ticket, título, técnico, estado, tipo..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchTerm('')}>
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={loadClienteBitacora}
                      startIcon={<RefreshIcon />}
                    >
                      Actualizar
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Tabs de estado */}
              <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                sx={{ mb: 2 }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Todos" />
                <Tab label="Activos" />
                <Tab label="Resueltos" />
                <Tab label="Cerrados" />
              </Tabs>

              {/* Lista de tickets */}
              {filteredTickets.length === 0 ? (
                <Alert severity="info">
                  {searchTerm ? 'No se encontraron tickets con esa búsqueda' : 'No hay tickets para este cliente'}
                </Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ticket</TableCell>
                        <TableCell>Título</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Técnico</TableCell>
                        <TableCell>Prioridad</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTickets.map((ticket) => (
                        <TableRow key={ticket.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {ticket.ticket_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {ticket.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getTicketTypeLabel(ticket.ticket_type)}
                              size="small"
                              variant="outlined"
                              icon={<span>{getTicketTypeIcon(ticket.ticket_type)}</span>}
                            />
                          </TableCell>
                          <TableCell>
                            {ticket.assigned_technician_name || 'Sin asignar'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={ticket.priority || 'N/A'}
                              color={getPriorityColor(ticket.priority)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(ticket.status)}
                              color={getStatusColor(ticket.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDate(ticket.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Ver detalles">
                              <IconButton
                                size="small"
                                onClick={() => handleViewTicket(ticket)}
                              >
                                👁️
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de detalle del ticket seleccionado */}
      <Dialog
        open={showTicketDetail}
        onClose={() => setShowTicketDetail(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">
              {selectedTicket?.ticket_number}
            </Typography>
            {selectedTicket?.status && (
              <Chip
                label={getStatusLabel(selectedTicket.status)}
                color={getStatusColor(selectedTicket.status)}
                size="small"
              />
            )}
            {selectedTicket?.priority && (
              <Chip
                label={selectedTicket.priority}
                color={getPriorityColor(selectedTicket.priority)}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {selectedTicket.title}
              </Typography>
              <Divider sx={{ my: 1 }} />

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Cliente
                  </Typography>
                  <Typography variant="body2">
                    {selectedTicket.customer_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Técnico
                  </Typography>
                  <Typography variant="body2">
                    {selectedTicket.assigned_technician_name || 'Sin asignar'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tipo
                  </Typography>
                  <Typography variant="body2">
                    {getTicketTypeLabel(selectedTicket.ticket_type)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Fecha creación
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedTicket.created_at)}
                  </Typography>
                </Grid>
              </Grid>

              {selectedTicket.resolution_notes && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Notas de resolución
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.resolution_notes}
                  </Typography>
                </>
              )}

              {selectedTicket.comments && selectedTicket.comments.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Comentarios
                  </Typography>
                  {selectedTicket.comments.slice(-3).map((comment, idx) => (
                    <Paper key={idx} sx={{ p: 1, mb: 1, bgcolor: '#f5f5f5' }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>{comment.user_name}</strong> - {formatDate(comment.created_at)}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {comment.comment}
                      </Typography>
                    </Paper>
                  ))}
                  {selectedTicket.comments.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      ... y {selectedTicket.comments.length - 3} comentarios más
                    </Typography>
                  )}
                </>
              )}

              {selectedTicket.materials_used?.materiales_incidente && 
               selectedTicket.materials_used.materiales_incidente.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Materiales utilizados
                  </Typography>
                  {selectedTicket.materials_used.materiales_incidente.map((m, idx) => (
                    <Chip 
                      key={idx} 
                      label={`${m.nombre} x${m.cantidad}`} 
                      size="small" 
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTicketDetail(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
